/**
 * Final Video — the STITCHED, PLAYABLE CUT (ui-tools fleet, Final Video
 * agent). final-video.md + the 2026-06-26 user yap.
 *
 * Final Video is the stitched-up version of the storyboard: every
 * storyboard frame becomes a video CLIP (`StudioClip`, `type:"video"`),
 * and the clips play one-by-one as the assembled cut. It SHARES the
 * `store.clips` model with the Timeline (the Timeline arranges the same
 * clips on a time axis; Final Video plays them back) — see the
 * `StudioClip` contract in StudioStore.
 *
 *   - EMPTY: a poster stage + "Render film" CTA, gated until the
 *     storyboard is ready (every scene boarded) and there is at least
 *     one frame with an image.
 *   - RENDERING: a believable per-scene pipeline (stitching shots →
 *     audio → color) with per-scene progress bars. Faked (no real
 *     encode), staged scene-by-scene, then the player lights up.
 *   - READY: a 16:9 PLAYER that plays the clips one-by-one with a
 *     simulated playhead, plus a scene-chapter scrub beneath (storyboard
 *     thumbnails, per-scene runtimes, click to jump). PER-CLIP controls
 *     on the focused clip: DELETE (deleteClip), RE-TAKE (retakeClip +
 *     runGeneration stub, loading→ready), and a REFERENCE VIDEO upload
 *     that guides the retake (clip.referenceVideo). Delete / retake mark
 *     the cut STALE → a "re-stitch" affordance. An export / share
 *     surface, and a premiere "it's a wrap!" moment that dispatches
 *     `koko:premiere` for the Koko bird to cheer.
 */

import { BasePanel } from "./BasePanel";
import {
  buildHelperParagraph,
  buildLabeledField,
  buildPixelBox,
  buildStudioButton,
} from "./dom";
import type {
  StoryboardFrame,
  StudioClip,
  StudioScene,
  StudioStore,
} from "../StudioStore";
import { openFormModal } from "../shared/Modal";
import { runGeneration } from "../shared/studioGeneration";
import "../styles/final-video.css";

interface FinalVideoPanelOptions {
  store: StudioStore;
}

/** The believable per-scene render stages. */
const STAGES = ["stitching shots", "scoring audio", "color grade"] as const;
const STAGE_MS = 480;
/** Visible per-clip playback step (cut is sped up so it's watchable). */
const PLAY_STEP_MS = 1100;
/** Reference videos above this many chars fall back to a light marker
 *  so we never blow the localStorage quota with a huge data URL. */
const MAX_REF_DATAURL = 600_000;

export class FinalVideoPanel extends BasePanel {
  private readonly store: StudioStore;
  private contentEl: HTMLDivElement | null = null;
  private storeUnsubscribers: (() => void)[] = [];
  private timers: number[] = [];

  /** sceneId → 0..STAGES.length progress while rendering. */
  private renderProgress = new Map<string, number>();
  /** clipIds currently regenerating (retake in flight). */
  private retaking = new Set<string>();
  /** Cut needs a re-stitch (a clip was deleted/retaken since last render). */
  private cutStale = false;
  private premiereFired = false;

  // Playback state.
  private currentClipIndex = 0;
  private playing = false;
  private playTimer: number | null = null;

  // Live player element refs (so playback ticks don't rebuild the DOM).
  private stageImgEl: HTMLDivElement | null = null;
  private stageBadgeEl: HTMLSpanElement | null = null;
  private playLabelEl: HTMLSpanElement | null = null;
  private progressFillEl: HTMLDivElement | null = null;
  private timeLabelEl: HTMLSpanElement | null = null;

  constructor(options: FinalVideoPanelOptions) {
    super();
    this.store = options.store;
  }

  protected buildRootElement(): HTMLElement {
    const root = document.createElement("div");
    root.className = "fv-root";
    const box = buildPixelBox();
    box.fill.appendChild(
      buildHelperParagraph(
        "Your cut. Every storyboard frame becomes a clip — render the film, watch it stitch scene by scene, then play it back, retake any clip that's off (with a reference video to guide it), and export.",
      ),
    );
    const content = document.createElement("div");
    content.className = "fv-content";
    box.fill.appendChild(content);
    this.contentEl = content;
    root.appendChild(box.outer);
    return root;
  }

  protected onMounted(): void {
    for (const key of [
      "scenes",
      "storyboardStatus",
      "videoStatus",
      "storyboardFramesBySceneId",
      "clips",
    ] as const) {
      this.storeUnsubscribers.push(this.store.subscribe(key, () => this.render()));
    }
    this.render();
  }

  protected onShown(): void {
    this.render();
  }

  protected onBeforeDestroy(): void {
    for (const unsub of this.storeUnsubscribers) unsub();
    this.storeUnsubscribers = [];
    this.stopPlayback();
    this.clearTimers();
  }

  private clearTimers(): void {
    this.timers.forEach((t) => window.clearTimeout(t));
    this.timers = [];
  }

  // ------------------------------------------------------------------
  // Clip helpers (shared StudioClip model)
  // ------------------------------------------------------------------

  /** Video clips that make up the cut, in play order. */
  private videoClips(): StudioClip[] {
    return this.store
      .get("clips")
      .filter((c) => c.type === "video")
      .slice()
      .sort((a, b) => a.startSec - b.startSec);
  }

  /** Ordered (scene, frame) pairs for boarded frames (those with images). */
  private boardedFrames(): Array<{ scene: StudioScene; frame: StoryboardFrame }> {
    const scenes = this.store.get("scenes");
    const framesMap = this.store.get("storyboardFramesBySceneId");
    const out: Array<{ scene: StudioScene; frame: StoryboardFrame }> = [];
    for (const scene of scenes) {
      const frames = (framesMap[scene.id] ?? [])
        .slice()
        .sort((a, b) => a.order - b.order);
      for (const frame of frames) {
        if (frame.image) out.push({ scene, frame });
      }
    }
    return out;
  }

  private canRender(): boolean {
    return (
      this.store.get("storyboardStatus") === "ready" &&
      this.boardedFrames().length > 0
    );
  }

  private isStale(): boolean {
    return this.cutStale || this.videoClips().some((c) => c.ready === false);
  }

  // ------------------------------------------------------------------
  // Render dispatch
  // ------------------------------------------------------------------

  private render(): void {
    if (!this.contentEl) return;
    // Drop stale element refs before a rebuild.
    this.stageImgEl = null;
    this.stageBadgeEl = null;
    this.playLabelEl = null;
    this.progressFillEl = null;
    this.timeLabelEl = null;

    this.contentEl.replaceChildren();
    const status = this.store.get("videoStatus");

    if (status === "rendering") {
      this.stopPlayback();
      this.contentEl.appendChild(this.buildRenderingView());
      return;
    }
    if (status === "ready" && this.videoClips().length > 0) {
      this.contentEl.appendChild(this.buildReadyView());
      return;
    }
    // empty (or "ready" but the cut was fully deleted)
    this.premiereFired = false;
    this.stopPlayback();
    this.contentEl.appendChild(this.buildEmptyView());
  }

  // ------------------------------------------------------------------
  // EMPTY
  // ------------------------------------------------------------------

  private buildEmptyView(): HTMLElement {
    const wrap = document.createElement("div");
    const scenes = this.store.get("scenes");
    const boarded = this.boardedFrames().length;

    const stage = document.createElement("div");
    stage.className = "fv-stage is-empty";
    stage.textContent =
      scenes.length === 0
        ? "No scenes to render yet"
        : boarded === 0
          ? "Board your scenes, then render the cut"
          : `${boarded} clip${boarded === 1 ? "" : "s"} ready to stitch`;
    wrap.appendChild(stage);

    const cta = buildStudioButton("● Render film", {
      variant: "primary",
      onClick: () => this.startRender(),
    });
    cta.dataset.kokoAction = "render-video";
    cta.disabled = !this.canRender();
    cta.title = this.canRender()
      ? "Stitch the storyboard frames into the cut"
      : "Finish the storyboard first — every scene needs ready frames.";
    wrap.appendChild(cta);

    if (!this.canRender() && scenes.length > 0) {
      const hint = document.createElement("p");
      hint.className = "fv-hint";
      hint.textContent =
        boarded === 0
          ? "No boarded frames yet — generate storyboard frames to build clips from."
          : "Storyboard isn't ready — board every scene to unlock rendering.";
      wrap.appendChild(hint);
    }
    return wrap;
  }

  // ------------------------------------------------------------------
  // RENDERING (faked per-scene pipeline)
  // ------------------------------------------------------------------

  private buildRenderingView(): HTMLElement {
    const wrap = document.createElement("div");
    const stage = document.createElement("div");
    stage.className = "fv-stage is-rendering";
    stage.textContent = "Stitching your cut…";
    wrap.appendChild(stage);

    const scenesWithClips = this.scenesWithClips();
    const list = document.createElement("div");
    list.className = "fv-render-list";
    scenesWithClips.forEach(({ scene }, i) => {
      const step = this.renderProgress.get(scene.id) ?? 0;
      const row = document.createElement("div");
      row.className = "fv-render-row";
      const label = document.createElement("span");
      label.className = "fv-render-label";
      label.textContent = `${i + 1}. ${scene.heading || "scene"}`;
      const bar = document.createElement("div");
      bar.className = "fv-render-bar";
      const fill = document.createElement("div");
      fill.className = "fv-render-fill";
      fill.style.width = `${Math.round((step / STAGES.length) * 100)}%`;
      bar.appendChild(fill);
      const state = document.createElement("span");
      state.className = "fv-render-state";
      state.textContent =
        step >= STAGES.length ? "done" : step === 0 ? "queued" : STAGES[step - 1];
      row.appendChild(label);
      row.appendChild(bar);
      row.appendChild(state);
      list.appendChild(row);
    });
    wrap.appendChild(list);
    return wrap;
  }

  /** Scenes that contributed at least one boarded frame, in order. */
  private scenesWithClips(): Array<{ scene: StudioScene; frames: StoryboardFrame[] }> {
    const seen = new Map<string, { scene: StudioScene; frames: StoryboardFrame[] }>();
    for (const { scene, frame } of this.boardedFrames()) {
      const entry = seen.get(scene.id) ?? { scene, frames: [] };
      entry.frames.push(frame);
      seen.set(scene.id, entry);
    }
    return Array.from(seen.values());
  }

  // ------------------------------------------------------------------
  // READY (player + chapter scrub + per-clip controls + export)
  // ------------------------------------------------------------------

  private buildReadyView(): HTMLElement {
    const wrap = document.createElement("div");
    const clips = this.videoClips();
    this.currentClipIndex = Math.min(this.currentClipIndex, clips.length - 1);
    if (this.currentClipIndex < 0) this.currentClipIndex = 0;
    const stale = this.isStale();

    // Premiere moment — fire once when a clean cut is ready.
    if (!stale && !this.premiereFired) {
      this.premiereFired = true;
      window.dispatchEvent(
        new CustomEvent("koko:premiere", {
          detail: {
            clips: clips.length,
            runtime: formatTime(this.totalRuntime()),
            line: "It's a wrap! Your cut is ready.",
          },
        }),
      );
    }

    const banner = document.createElement("div");
    banner.className = "fv-wrap-banner";
    banner.textContent = stale
      ? "Cut changed — re-stitch to refresh the premiere"
      : "🎬 It's a wrap!";
    if (stale) banner.classList.add("is-stale");
    wrap.appendChild(banner);

    // ---- Player stage ----
    const stage = document.createElement("div");
    stage.className = "fv-stage is-ready";
    stage.dataset.kokoAction = "video-play";

    const img = document.createElement("div");
    img.className = "fv-stage-img";
    stage.appendChild(img);
    this.stageImgEl = img;

    const badge = document.createElement("span");
    badge.className = "fv-stage-badge";
    stage.appendChild(badge);
    this.stageBadgeEl = badge;

    const playLabel = document.createElement("span");
    playLabel.className = "fv-play-label";
    stage.appendChild(playLabel);
    this.playLabelEl = playLabel;

    if (stale) {
      const staleTag = document.createElement("span");
      staleTag.className = "fv-stale-tag";
      staleTag.textContent = "● stale";
      stage.appendChild(staleTag);
    }

    stage.addEventListener("click", () => this.togglePlayback());
    wrap.appendChild(stage);

    // ---- Transport (progress + time) ----
    const transport = document.createElement("div");
    transport.className = "fv-transport";
    const progress = document.createElement("div");
    progress.className = "fv-progress";
    const progressFill = document.createElement("div");
    progressFill.className = "fv-progress-fill";
    progress.appendChild(progressFill);
    this.progressFillEl = progressFill;
    const time = document.createElement("span");
    time.className = "fv-time";
    this.timeLabelEl = time;
    transport.appendChild(progress);
    transport.appendChild(time);
    wrap.appendChild(transport);

    // ---- Chapter scrub (scenes → clip thumbnails) ----
    wrap.appendChild(this.buildChapters(clips));

    // ---- Per-clip controls (focused clip) ----
    wrap.appendChild(this.buildClipControls(clips));

    // ---- Re-stitch (when stale) + export/share ----
    wrap.appendChild(this.buildBottomBar(stale));

    // Paint the initial playback state into the live refs.
    this.syncPlayerUi();
    return wrap;
  }

  private buildChapters(clips: StudioClip[]): HTMLElement {
    const chapters = document.createElement("div");
    chapters.className = "fv-chapters";

    for (const { scene } of this.scenesWithClips()) {
      const sceneClips = this.store
        .getClipsForScene(scene.id)
        .filter((c) => c.type === "video")
        .sort((a, b) => a.startSec - b.startSec);
      if (sceneClips.length === 0) continue;

      const group = document.createElement("div");
      group.className = "fv-chapter-group";
      const head = document.createElement("div");
      head.className = "fv-chapter-head";
      const sceneRuntime = sceneClips.reduce((s, c) => s + c.durationSec, 0);
      head.textContent = `${scene.heading || "scene"} · ${formatTime(sceneRuntime)}`;
      group.appendChild(head);

      const strip = document.createElement("div");
      strip.className = "fv-chapter-strip";
      for (const clip of sceneClips) {
        const flatIndex = clips.findIndex((c) => c.id === clip.id);
        const idx = flatIndex >= 0 ? flatIndex : 0;
        const chap = document.createElement("button");
        chap.type = "button";
        chap.className = "fv-chapter";
        chap.dataset.kokoAction = "video-chapter";
        chap.dataset.sceneId = scene.id;
        chap.dataset.clipId = clip.id;
        if (idx === this.currentClipIndex) chap.classList.add("is-current");

        const thumb = document.createElement("div");
        thumb.className = "fv-chapter-thumb";
        const imageUrl = clip.content?.image ?? null;
        if (this.retaking.has(clip.id)) {
          thumb.classList.add("is-loading");
          thumb.textContent = "retaking…";
        } else if (imageUrl) {
          thumb.style.backgroundImage = `url("${imageUrl}")`;
        } else {
          thumb.style.background = `hsl(${hashHue(clip.id)} 45% 35%)`;
        }
        if (clip.ready === false) thumb.classList.add("is-pending");

        const meta = document.createElement("span");
        meta.className = "fv-chapter-meta";
        meta.textContent = `${idx + 1} · ${formatTime(clip.durationSec)}`;
        if (clip.referenceVideo) {
          const ref = document.createElement("span");
          ref.className = "fv-chapter-ref";
          ref.textContent = "⛓";
          ref.title = "Has a reference video";
          meta.appendChild(ref);
        }
        if ((clip.retakeCount ?? 0) > 0) {
          const rc = document.createElement("span");
          rc.className = "fv-chapter-retakes";
          rc.textContent = `↺${clip.retakeCount}`;
          meta.appendChild(rc);
        }

        chap.appendChild(thumb);
        chap.appendChild(meta);
        chap.addEventListener("click", () => this.seekTo(idx));
        strip.appendChild(chap);
      }
      group.appendChild(strip);
      chapters.appendChild(group);
    }
    return chapters;
  }

  private buildClipControls(clips: StudioClip[]): HTMLElement {
    const bar = document.createElement("div");
    bar.className = "fv-clip-controls";
    const clip = clips[this.currentClipIndex];
    if (!clip) return bar;

    const label = document.createElement("span");
    label.className = "fv-clip-label";
    const scene = this.store.get("scenes").find((s) => s.id === clip.sceneId);
    label.textContent = `Clip ${this.currentClipIndex + 1} / ${clips.length}${
      scene ? ` · ${scene.heading}` : ""
    }`;
    bar.appendChild(label);

    const actions = document.createElement("div");
    actions.className = "fv-clip-actions";

    const retaking = this.retaking.has(clip.id);

    const retake = buildStudioButton(retaking ? "Retaking…" : "↺ Retake", {
      onClick: () => this.openClipModal(clip.id, true),
    });
    retake.dataset.kokoAction = "video-clip-retake";
    retake.dataset.clipId = clip.id;
    retake.disabled = retaking;
    retake.title = clip.referenceVideo
      ? "Regenerate this clip, guided by the reference video"
      : "Regenerate this clip";
    actions.appendChild(retake);

    const ref = buildStudioButton(
      clip.referenceVideo ? "⛓ Reference ✓" : "⛓ Reference video",
      { onClick: () => this.openClipModal(clip.id, false) },
    );
    ref.dataset.kokoAction = "video-clip-reference";
    ref.dataset.clipId = clip.id;
    ref.disabled = retaking;
    ref.title = clip.referenceVideo
      ? `Reference: ${describeRef(clip.referenceVideo)} — click to replace`
      : "Upload a reference video to guide the retake";
    if (clip.referenceVideo) ref.classList.add("is-set");
    actions.appendChild(ref);

    const del = buildStudioButton("✕ Delete", {
      variant: "danger",
      onClick: () => this.deleteClip(clip.id),
    });
    del.dataset.kokoAction = "video-clip-delete";
    del.dataset.clipId = clip.id;
    del.disabled = retaking;
    actions.appendChild(del);

    bar.appendChild(actions);

    if (clip.referenceVideo) {
      const refNote = document.createElement("div");
      refNote.className = "fv-clip-refnote";
      refNote.textContent = `Reference video: ${describeRef(clip.referenceVideo)}`;
      bar.appendChild(refNote);
    }
    return bar;
  }

  private buildBottomBar(stale: boolean): HTMLElement {
    const controls = document.createElement("div");
    controls.className = "fv-controls";

    if (stale) {
      const restitch = buildStudioButton("⟳ Re-stitch the cut", {
        variant: "primary",
        onClick: () => this.startRender(),
      });
      restitch.dataset.kokoAction = "video-restitch";
      controls.appendChild(restitch);
    } else {
      const hint = document.createElement("span");
      hint.className = "fv-hint";
      hint.textContent = `Final cut · ${formatTime(this.totalRuntime())} · ${
        this.videoClips().length
      } clips`;
      controls.appendChild(hint);
    }

    const exportWrap = document.createElement("div");
    exportWrap.className = "fv-export";
    const download = buildStudioButton("⤓ Export", {
      variant: "primary",
      onClick: () => this.openExportModal(),
    });
    download.dataset.kokoAction = "video-export";
    download.disabled = stale;
    download.title = stale ? "Re-stitch the cut before exporting" : "Export the cut";
    exportWrap.appendChild(download);
    controls.appendChild(exportWrap);
    return controls;
  }

  // ------------------------------------------------------------------
  // Playback (simulated, clip-by-clip)
  // ------------------------------------------------------------------

  private togglePlayback(): void {
    if (this.playing) this.stopPlayback();
    else this.startPlayback();
  }

  private startPlayback(): void {
    const clips = this.videoClips();
    if (clips.length === 0) return;
    this.playing = true;
    // Restart from the top if we were parked on the last clip.
    if (this.currentClipIndex >= clips.length - 1) this.currentClipIndex = 0;
    this.syncPlayerUi();
    this.scheduleAdvance();
  }

  private scheduleAdvance(): void {
    if (this.playTimer !== null) window.clearTimeout(this.playTimer);
    this.playTimer = window.setTimeout(() => {
      const clips = this.videoClips();
      if (this.currentClipIndex >= clips.length - 1) {
        this.stopPlayback();
        return;
      }
      this.currentClipIndex += 1;
      this.syncPlayerUi();
      this.scheduleAdvance();
    }, PLAY_STEP_MS);
  }

  private stopPlayback(): void {
    this.playing = false;
    if (this.playTimer !== null) {
      window.clearTimeout(this.playTimer);
      this.playTimer = null;
    }
    this.syncPlayerUi();
  }

  private seekTo(index: number): void {
    const clips = this.videoClips();
    if (clips.length === 0) return;
    this.currentClipIndex = Math.max(0, Math.min(index, clips.length - 1));
    this.syncPlayerUi();
  }

  /** Paint the live player refs (no DOM rebuild) for the current clip. */
  private syncPlayerUi(): void {
    const clips = this.videoClips();
    const clip = clips[this.currentClipIndex];
    if (this.stageImgEl) {
      const url = clip?.content?.image ?? null;
      this.stageImgEl.style.backgroundImage = url ? `url("${url}")` : "";
      this.stageImgEl.classList.toggle("is-blank", !url);
    }
    if (this.stageBadgeEl) {
      const scene = this.store.get("scenes").find((s) => s.id === clip?.sceneId);
      this.stageBadgeEl.textContent = clip
        ? `${this.currentClipIndex + 1}/${clips.length}${scene ? ` · ${scene.heading}` : ""}`
        : "";
    }
    if (this.playLabelEl) {
      this.playLabelEl.textContent = this.playing ? "❚❚ playing" : "▶ play";
    }
    if (this.progressFillEl) {
      const pct = clips.length
        ? ((this.currentClipIndex + 1) / clips.length) * 100
        : 0;
      this.progressFillEl.style.width = `${pct}%`;
    }
    if (this.timeLabelEl) {
      const elapsed = clips
        .slice(0, this.currentClipIndex + 1)
        .reduce((s, c) => s + c.durationSec, 0);
      this.timeLabelEl.textContent = `${formatTime(elapsed)} / ${formatTime(
        this.totalRuntime(),
      )}`;
    }
    // Reflect the focused clip on the chapter strip without a full rebuild
    // while playing.
    const root = this.contentEl;
    if (root) {
      root.querySelectorAll<HTMLButtonElement>(".fv-chapter").forEach((btn) => {
        btn.classList.toggle("is-current", btn.dataset.clipId === clip?.id);
      });
    }
  }

  private totalRuntime(): number {
    return this.videoClips().reduce((s, c) => s + c.durationSec, 0);
  }

  // ------------------------------------------------------------------
  // Per-clip mutations (shared store APIs)
  // ------------------------------------------------------------------

  private deleteClip(clipId: string): void {
    this.stopPlayback();
    this.store.deleteClip(clipId);
    this.cutStale = true;
    // currentClipIndex is clamped on the next render.
    this.render();
  }

  private async retakeClip(clipId: string): Promise<void> {
    if (this.retaking.has(clipId)) return;
    const clip = this.store.get("clips").find((c) => c.id === clipId);
    if (!clip) return;
    this.stopPlayback();
    this.retaking.add(clipId);
    // Bump retake count + mark not-ready (keeps the existing reference).
    this.store.retakeClip(clipId);
    this.cutStale = true;
    this.render();

    const scene = this.store.get("scenes").find((s) => s.id === clip.sceneId);
    const direction = this.store.getVisualDirection();
    const prompt = [
      scene?.heading,
      clip.content?.text ?? "",
      clip.referenceVideo
        ? `guided by reference: ${describeRef(clip.referenceVideo)}`
        : "",
      direction.notes.join(", "),
    ]
      .filter(Boolean)
      .join(" · ");

    const result = await runGeneration({
      kind: "image",
      prompt,
      seed: `${clipId}:${(clip.retakeCount ?? 0) + 1}`,
      palette: direction.palette,
      aspect: "16:9",
    });

    this.retaking.delete(clipId);
    // The clip may have been deleted mid-flight; guard.
    if (!this.store.get("clips").some((c) => c.id === clipId)) {
      this.render();
      return;
    }
    this.store.updateClip(clipId, {
      content: { ...(clip.content ?? {}), image: result.url },
      ready: true,
    });
    this.render();
  }

  private openClipModal(clipId: string, retake: boolean): void {
    const clip = this.store.get("clips").find((entry) => entry.id === clipId);
    if (!clip) return;
    let pendingReference = clip.referenceVideo ?? null;
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "video/*";
    input.className = "studio-input";
    input.dataset.kokoAction = "video-clip-reference-input";
    const preview = document.createElement("div");
    preview.className = "fv-modal-preview";
    const renderPreview = (): void => {
      preview.textContent = pendingReference
        ? `Reference ready · ${describeRef(pendingReference)}`
        : "No reference video — the current clip and visual direction will guide the retake.";
    };
    renderPreview();
    input.addEventListener("change", () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = typeof reader.result === "string" ? reader.result : "";
        pendingReference =
          dataUrl.length > MAX_REF_DATAURL || !dataUrl ? `uploaded:${file.name}` : dataUrl;
        renderPreview();
      };
      reader.onerror = () => {
        pendingReference = `uploaded:${file.name}`;
        renderPreview();
      };
      reader.readAsDataURL(file);
    });
    const modal = openFormModal({
      title: retake ? "Retake clip" : "Reference video",
      description: retake
        ? "Optionally add a motion or performance reference before regenerating this clip."
        : "Attach a reference now; it will guide the next retake.",
      surface: retake ? "video-retake" : "video-reference",
      variant: "is-entity-form",
      initialFocus: input,
      submitLabel: retake ? "Start retake" : "Save reference",
      submitAction: retake ? "video-retake-save" : "video-reference-save",
      cancelAction: retake ? "video-retake-cancel" : "video-reference-cancel",
      onSubmit: () => {
        if (pendingReference !== clip.referenceVideo) {
          this.store.updateClip(clipId, { referenceVideo: pendingReference });
        }
        if (retake) void this.retakeClip(clipId);
        else this.render();
        return true;
      },
    });
    modal.root.dataset.clipId = clipId;
    modal.body.append(preview, buildLabeledField("Reference video (optional)", input));
  }

  private openExportModal(): void {
    const format = document.createElement("select");
    format.className = "studio-select fv-format";
    format.dataset.kokoAction = "video-format";
    ["MP4 · 1080p", "MP4 · 4K", "WebM · 1080p", "Vertical · 9:16"].forEach((label) => {
      const option = document.createElement("option");
      option.textContent = label;
      format.appendChild(option);
    });
    const modal = openFormModal({
      title: "Export final cut",
      description: `${this.videoClips().length} clips · ${formatTime(this.totalRuntime())} total runtime`,
      surface: "video-export",
      variant: "is-entity-form",
      initialFocus: format,
      submitLabel: "Export",
      submitAction: "video-export-save",
      cancelAction: "video-export-cancel",
      onSubmit: () => true,
    });
    const summary = document.createElement("div");
    summary.className = "fv-modal-preview";
    summary.textContent = "The stitched cut includes the current clip order, color pass, score, and dialogue mix.";
    const link = buildStudioButton("🔗 Copy share link", {});
    link.dataset.kokoAction = "video-copy-link";
    link.addEventListener("click", () => {
      link.textContent = "✓ Link copied";
    });
    modal.footer.insertBefore(link, modal.cancelButton);
    modal.body.append(summary, buildLabeledField("Format", format));
  }

  // ------------------------------------------------------------------
  // Render pipeline (stitch frames → clips, faked per-scene stages)
  // ------------------------------------------------------------------

  private startRender(): void {
    const stitched = this.stitchVideoClips();
    if (stitched.length === 0) return;
    this.stopPlayback();
    this.clearTimers();

    // Write the (not-yet-ready) clip list, preserving non-video tracks.
    const nonVideo = this.store.get("clips").filter((c) => c.type !== "video");
    this.store.setClips([...nonVideo, ...stitched]);

    const scenesWithClips = this.scenesWithClips();
    this.renderProgress = new Map(scenesWithClips.map(({ scene }) => [scene.id, 0]));
    this.store.setVideoStatus("rendering");

    let tick = 0;
    for (let i = 0; i < scenesWithClips.length; i += 1) {
      for (let s = 1; s <= STAGES.length; s += 1) {
        tick += 1;
        const sceneId = scenesWithClips[i].scene.id;
        const stageStep = s;
        const t = window.setTimeout(() => {
          this.renderProgress.set(sceneId, stageStep);
          if (this.store.get("videoStatus") === "rendering") this.render();
        }, tick * STAGE_MS);
        this.timers.push(t);
      }
    }
    const done = window.setTimeout(
      () => {
        // Mark every video clip ready; mirror scene.videoReady.
        const clips = this.store
          .get("clips")
          .map((c) => (c.type === "video" ? { ...c, ready: true } : c));
        this.store.setClips(clips);
        for (const { scene } of scenesWithClips) {
          if (!scene.videoReady) {
            this.store.updateScene(scene.id, scene.body, { videoReady: true });
          }
        }
        this.cutStale = false;
        this.premiereFired = false;
        this.currentClipIndex = 0;
        this.store.setVideoStatus("ready");
      },
      (tick + 1) * STAGE_MS,
    );
    this.timers.push(done);
  }

  /**
   * Build the merged video-clip list from boarded frames, PRESERVING the
   * id / referenceVideo / retakeCount of any existing clip that already
   * maps to the same storyboard frame (so retakes + references survive a
   * re-stitch). Sequential startSec along the cut.
   */
  private stitchVideoClips(): StudioClip[] {
    const existingByFrame = new Map<string, StudioClip>();
    for (const c of this.store.get("clips")) {
      if (c.type === "video" && c.frameId) existingByFrame.set(c.frameId, c);
    }
    const out: StudioClip[] = [];
    let cursor = 0;
    for (const { scene, frame } of this.boardedFrames()) {
      const durationSec = clipDuration(frame);
      const prior = existingByFrame.get(frame.id);
      const clip: StudioClip = {
        id: prior?.id ?? genId("clip"),
        sceneId: scene.id,
        frameId: frame.id,
        startSec: cursor,
        durationSec,
        type: "video",
        content: { image: frame.image ?? null, text: frame.caption },
        retakeCount: prior?.retakeCount ?? 0,
        referenceVideo: prior?.referenceVideo ?? null,
        ready: false,
      };
      out.push(clip);
      cursor += durationSec;
    }
    return out;
  }
}

/** Per-frame screen time, derived from caption length, clamped 3–12s. */
function clipDuration(frame: StoryboardFrame): number {
  const len = (frame.caption ?? "").replace(/\s+/g, " ").trim().length;
  return Math.max(3, Math.min(12, Math.round(len / 12) + 3));
}

function formatTime(totalSec: number): string {
  const secs = Math.max(0, Math.round(totalSec));
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Human-readable label for a stored reference-video value. */
function describeRef(value: string): string {
  if (value.startsWith("uploaded:")) return value.slice("uploaded:".length);
  if (value.startsWith("data:")) return "uploaded clip";
  return value;
}

function hashHue(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return Math.abs(h) % 360;
}

function genId(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}${Math.floor(Math.random() * 1e4).toString(36)}`;
}
